import { isAuditSectionKey } from '@behuman/core-contract'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { isGone, isNotFound, OperationOutcomeError } from '@medplum/core'
import { itemsMap } from './components/PatientChart.utils.tsx'
import { usePatientChartCounts } from '@/hooks/usePatientChartCounts'
import { usePatientCareTasks } from './components/CareCoordinationTasks/usePatientCareTasks'
import { useClient } from '@/hooks/useClient'
import { logError, logWarning } from '@/utils/errorUtils'
import { MessagesActionButton } from './components/Messages/MessagesActionButton.tsx'
import { MessagesCollapsableContainer } from './components/Messages/MessagesCollapsableContainer.tsx'
import { forgetSelectedThread } from '@/pages/Messages/useMessagesPageState'
import { usePatientSidePanel } from './components/PatientSidePanelContext.tsx'
import { PatientChartSidePanelHost } from './components/PatientChartSidePanelHost.tsx'
import { PATIENT_SIDE_PANEL_WIDTH } from './components/PatientSidePanel.tsx'
import { PatientChartLayout } from './components/PatientChartLayout.tsx'
import { EncounterWorkspace, SIDE_PANEL_WIDTH } from '@/pages/Call/EncounterWorkspace'
import { Button, Loader } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { usePatientMergeRedirect } from '@/hooks/usePatientMerge'
import { useRecentPatients } from '@/hooks/useRecentPatients'
import { formatPatientName } from '@/utils'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from './chartTopOffset'
import { useCallSession } from '@/components/session'
import {
  InterruptedResumePanel,
  type InterruptedResumeBusy,
} from '@/components/scribe/InterruptedResumePanel'
import {
  SUPPORT_LEGACY_PATIENT_CHART_RESOURCE_PATHS,
  buildPatientChartRelativePath,
  parsePatientChartSplat,
} from './patientChartPath'
import { useAuditSectionReporting } from './useAuditSectionReporting'
import { useAuditPrintReporting } from './useAuditPrintReporting'

// Total fixed/sticky height stacked above the chart content area:
//   64px Shell header
// + <RecentPatientsBar>      (38px, published as --recent-bar-height when visible)
// + <PatientChartActionBar>  (76px, published as --patient-header-height)
//
// We compose it as a CSS expression rather than a JS number so the chart
// sidebar reflows automatically when the RECENT bar mounts/unmounts (the
// previous hardcoded `140px` ignored the 38px RECENT bar). See
// `./chartTopOffset.ts` for the full breakdown.

/** A read that failed because the record is gone (404/410), not because the caller may not see it. */
function isGoneOrNotFound(err: unknown): boolean {
  return err instanceof OperationOutcomeError && (isNotFound(err.outcome) || isGone(err.outcome))
}

export const PatientPage = () => {
  const params = useParams()
  const navigate = useNavigate()
  const medplum = useMedplum()
  const { recordPatient } = useRecentPatients()

  const [patient, setPatient] = useState<Patient>()
  const [isPatientLoading, setIsPatientLoading] = useState(false)
  // PD-1986 — set when the id on the URL is a merged (`replaced-by` link, `read` kept) or deleted
  // (read gone) record: the middleware says where its chart went. `notFound` is the answer
  // "nowhere we can show you" for a record that is gone.
  const [mergeProbe, setMergeProbe] = useState<{ id: string; read?: Patient }>()
  const [notFound, setNotFound] = useState(false)
  // The redirect check failed: the record was merged or deleted, so its chart is never opened as a
  // live one in the meantime (anything saved on a retired chart is deleted with it) — Retry instead.
  const [redirectFailed, setRedirectFailed] = useState(false)
  // A retired (`replaced-by`) record with nowhere this caller can follow it to — the kept record is
  // not readable to them: shown closed, never as a live chart (its records are deleted with it).
  const [retiredClosed, setRetiredClosed] = useState(false)
  const patientId = params.patientId ?? ''
  // The number of the newest read: an answer from any earlier one is stale.
  const latestRead = useRef(0)
  const probing = mergeProbe?.id === patientId
  const mergeRedirect = usePatientMergeRedirect(patientId, probing)
  // Waiting on the answer: show the loader, never a previous patient's chart under this URL.
  const awaitingRedirect = probing && !notFound && patient?.id !== patientId

  useEffect(() => {
    if (mergeProbe?.id !== patientId || !(mergeRedirect.data || mergeRedirect.isError)) return
    // A cached answer may be out of date (restored, then merged elsewhere): wait for the fresh one.
    if (mergeRedirect.isFetching) return
    // A failed refresh leaves the cached answer in `data`, possibly out of date: follow none, and
    // open nothing either.
    if (mergeRedirect.isError) {
      setRedirectFailed(true)
      return
    }
    const keptId = mergeRedirect.data?.keptPatientId
    // Never redirect to the id we are already on.
    if (!keptId || keptId === patientId) {
      // A readable record here is a retired one (`replaced-by`): kept closed. A gone one is "not
      // found".
      if (mergeProbe.read) setRetiredClosed(true)
      else setNotFound(true)
      return
    }
    showNotification({
      message: mergeRedirect.data?.keptPatientName
        ? `This record was merged into ${mergeRedirect.data.keptPatientName}.`
        : 'This record was merged into another record.',
    })
    // Neither the previous chart nor this merged record may render under the kept URL while its
    // read is pending — the loader shows from now, not only once that read has started.
    setPatient(undefined)
    setIsPatientLoading(true)
    void navigate(`/patients/${keptId}`, { replace: true })
  }, [
    mergeProbe,
    patientId,
    mergeRedirect.data,
    mergeRedirect.isError,
    mergeRedirect.isFetching,
    navigate,
  ])

  useEffect(() => {
    fetchPatient()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, medplum])

  // Rewrite legacy mid-path deep links (`:resourceId/:section`) to the canonical
  // `:section/:resourceId` form when support is enabled; always warn so residual
  // usage is visible after the flag is flipped off.
  useEffect(() => {
    const splat = params['*']
    const parsed = parsePatientChartSplat(splat)
    if (!parsed.isLegacy) return

    logWarning('patient', 'Legacy chart deep link', splat || '')
    if (SUPPORT_LEGACY_PATIENT_CHART_RESOURCE_PATHS && parsed.resourceId) {
      void navigate(buildPatientChartRelativePath(parsed.section, parsed.resourceId), {
        replace: true,
      })
    }
  }, [params, navigate])

  // Warm the AI clinical-summary overview on chart open. No force: the server
  // rebuilds only when the stored overview's details hash is stale and no
  // build is already in flight, so this is cheap when nothing changed.
  // Fire-and-forget — the chart must not wait on (or surface) it.
  const client = useClient()
  useEffect(() => {
    if (!params.patientId) return
    client.patientClinicalSummaryOverviewRefresh({ id: params.patientId }).catch((err) => {
      logError('patient', 'Failed to warm the clinical summary overview', err)
    })
  }, [client, params.patientId])

  // Record this patient in the "recent patients" list for the header pill row.
  useEffect(() => {
    if (!patient?.id) return
    const name = formatPatientName(patient.name?.[0]) || 'Patient'
    recordPatient({ id: patient.id, name })
  }, [patient?.id, patient?.name, recordPatient])

  const fetchPatient = () => {
    if (params.patientId) {
      const id = params.patientId
      // Every read is numbered: an answer from an earlier read — of another chart, or of this one
      // before a section change — must not overwrite what a newer read decided (R50).
      const readNo = ++latestRead.current
      const superseded = () => latestRead.current !== readNo
      setIsPatientLoading(true)
      setNotFound(false)
      setRedirectFailed(false)
      setRetiredClosed(false)
      setMergeProbe(undefined)
      // Ordinary navigation keeps the held chart mounted until the read lands (its right pane may
      // hold an in-progress note). Only the merge path below clears it.
      const startProbe = (probe: { id: string; read?: Patient }) => {
        // A retired chart is never left mounted (editable) while its redirect is looked up — not
        // even the same record's, held from before it was merged.
        setPatient(undefined)
        setMergeProbe(probe)
      }
      // Past the client cache: a copy cached before a merge has no `replaced-by` link.
      void medplum
        .readResource('Patient', id, { cache: 'no-cache' })
        .then((read) => {
          // A late answer for a chart navigated away from must not touch the current one (R50).
          if (superseded()) return
          // A merged record keeps a `replaced-by` link until it is deleted.
          if (read.link?.some((l) => l.type === 'replaced-by')) startProbe({ id, read })
          else setPatient(read)
        })
        .catch((err: unknown) => {
          if (superseded()) return
          // Only a gone record can have been merged away; other failures keep the blank screen.
          if (isGoneOrNotFound(err)) startProbe({ id })
          else logError('patient', 'Failed to load the patient', err)
        })
        .finally(() => {
          if (!superseded()) setIsPatientLoading(false)
        })
    }
  }

  if (probing && redirectFailed) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-sm text-[#7D7987]">
        Couldn&apos;t check where this record went.
        <Button
          variant="default"
          onClick={() => {
            // The record itself is read again too: it may have been restored meanwhile.
            fetchPatient()
            void mergeRedirect.refetch()
          }}
        >
          Retry
        </Button>
      </div>
    )
  }

  if (probing && retiredClosed) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[#7D7987]">
        This record was merged into another record.
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[#7D7987]">
        Patient not found
      </div>
    )
  }

  if (!patient || awaitingRedirect) {
    const loading = isPatientLoading || awaitingRedirect
    return (
      <div className="flex h-screen items-center justify-center gap-3 text-sm text-[#7D7987]">
        {loading && <Loader size="sm" />}
        {loading ? 'Loading patient chart...' : null}
      </div>
    )
  }

  // The host owns the side-panel state slot (panelState, openPanel, full-width
  // toggle, panel events) and the floating `<PatientSidePanel>` overlay mount.
  // We pass NO `onVirtualVisitSelect` so the chart's "New Encounter → Virtual
  // Visit" click follows the default `panelType: 'newEncounterSoap'` flow,
  // which the body renders inline as the main content (see comment at
  // PatientChartBody's render branch).
  return (
    <PatientChartSidePanelHost patient={patient}>
      <PatientChartBody patient={patient} refetchPatient={fetchPatient} />
    </PatientChartSidePanelHost>
  )
}

type PatientChartBodyProps = {
  patient: Patient
  refetchPatient: () => void
}

/**
 * Inner body that consumes `usePatientSidePanel()` so it can drive the chart
 * layout off the host's `panelState` + `isPanelFullWidth`. Lives as a sibling
 * of `<PatientChartSidePanelHost>` to ensure the hook resolves to the real
 * Provider value (rather than the no-op stub).
 */
function PatientChartBody({ patient, refetchPatient }: PatientChartBodyProps) {
  const params = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: fhirSectionCounts } = usePatientChartCounts(patient.id)
  // PD-1882 — Care Queue tasks aren't a FHIR search on the patient, so their counter
  // (open tasks, as in the design) comes from the section's own query, which the
  // section then reuses. It scans the org's boards on each chart load — accepted.
  const { data: careTasks } = usePatientCareTasks(patient.id)
  const openCareTasks = careTasks?.tasks.filter((t) => t.isOpen).length
  const sectionCounts =
    openCareTasks === undefined
      ? fhirSectionCounts
      : { ...fhirSectionCounts, 'care-coordination-tasks': openCareTasks }
  const [isMessagesPanelOpen, setIsMessagesPanelOpen] = useState(false)
  // The thread a `?messages=<threadId>` link (the daily briefing) asked to open.
  const [focusThreadId, setFocusThreadId] = useState<string | null>(null)
  const { panelState, openPanel, closePanel, emitPanelEvent, isPanelFullWidth, setPanelFullWidth } =
    usePatientSidePanel()
  const { interruptedSession, discardInterrupted } = useCallSession()
  const [resumeBusy, setResumeBusy] = useState<InterruptedResumeBusy>(null)

  // Honor the global header "New Encounter" hand-off: when the chart is
  // opened with `?newEncounter=1`, auto-open the encounter side panel and
  // strip the param so it doesn't re-trigger on refresh / back navigation.
  useEffect(() => {
    if (!patient.id) return
    if (searchParams.get('newEncounter') !== '1') return
    openPanel({ panelType: 'newEncounterSoap' })
    const next = new URLSearchParams(searchParams)
    next.delete('newEncounter')
    setSearchParams(next, { replace: true })
  }, [patient.id, searchParams, setSearchParams, openPanel])

  // `?messages=<threadId>` opens the messages panel at that thread, then leaves the URL.
  useEffect(() => {
    const threadId = searchParams.get('messages')
    if (!threadId || !patient.id) return
    forgetSelectedThread(patient.id)
    setFocusThreadId(threadId)
    setIsMessagesPanelOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('messages')
    setSearchParams(next, { replace: true })
  }, [patient.id, searchParams, setSearchParams])

  /**
   * `newEncounterSoap` is intentionally NOT rendered as a side-panel overlay.
   * Instead the Progress Note edit form (with its Zoom session) renders as a
   * `rightPane` flex sibling of the chart-content scroll container — the same
   * pattern CallPage uses. The chart pane keeps rendering whichever chart
   * section the user navigated to, so clicking the chart sidebar while a new
   * encounter is active swaps the chart pane content WITHOUT unmounting the
   * encounter workspace (which would tear down Zoom and lose form state).
   *
   * The `careCoordinator` flow continues to use the overlay side panel
   * mounted by the host — that path is unchanged.
   */
  const isNewEncounterActive = panelState.open && panelState.panelType === 'newEncounterSoap'

  const currentPage = parsePatientChartSplat(params['*']).section
  const currentItem = itemsMap.get(currentPage)

  // PD-1322 (S8) — report every chart section view from the frame (debounced,
  // once per section per visit). The server authors the audit record.
  // The section registry is the closed vocabulary the contract accepts, so a page slug
  // that is not one of its keys is filtered out here rather than rejected at the request.
  useAuditSectionReporting(
    patient.id,
    currentItem && isAuditSectionKey(currentPage) ? currentPage : undefined,
  )
  // PD-1322 (S18) — printing the chart is an audit event too (AC4/AC12). Mounted on the
  // frame rather than per section, because a print captures whatever is on screen.
  useAuditPrintReporting(patient.id)
  // Only the (now legacy) careCoordinator path uses the overlay side panel;
  // the new-encounter flow renders inline as the main content so we must NOT
  // squeeze the chart-item area when it is active.
  const sidePanelOverlayOpen = panelState.open && panelState.panelType === 'careCoordinator'
  const chartRightOffset = Math.max(
    sidePanelOverlayOpen && !isPanelFullWidth ? PATIENT_SIDE_PANEL_WIDTH : 0,
    isMessagesPanelOpen ? 700 : 0,
  )

  // Row 2 contract: chart sidebar + main content live inside a
  // `ScrollContext.Provider` so descendants (PatientChart sidebar, ListLayout
  // left rail, Documents / LabResults CCDA-TOC sticky bands, and
  // ResourceTable's sticky <thead>) pin BELOW the `<PatientChartActionBar>`.
  // The action bar itself is portaled to <body> from inside
  // `<PatientChartActionBar>`, so it stretches the FULL viewport width
  // regardless of Shell's `padding-right: var(--zoom-panel-width)`. The
  // `rightOffset` we forward lets the portaled fixed header narrow when the
  // Care Coordinator side panel or the Messages drawer is open and the
  // right-aligned action buttons would otherwise float over those overlays.
  //
  // The `.patientChart` class previously set `height: calc(100vh-64px)` via
  // `index.css`; with the action bar now in flow above this row, we override
  // it inline so the chart layout occupies exactly the viewport below the
  // combined Shell + action-bar stack.
  return (
    <>
      <PatientChartLayout
        patient={patient}
        sectionCounts={sectionCounts}
        currentPage={currentPage}
        onItemClick={(item) => {
          setPanelFullWidth(false)
          void navigate(item.dataKey)
        }}
        sidebarWidth={280}
        scrollContainerWidthSubtract={280}
        scrollContainerId="patient-chart-scroll-container"
        stickyTop={CHART_CONTENT_TOP_VAR}
        scrollContextScope="row"
        actionBarRightOffset={chartRightOffset}
        onPatientEdited={refetchPatient}
        outerClassName="patientChart relative flex"
        outerStyle={{ height: CHART_CONTENT_HEIGHT_VAR }}
        chartPaneClassName="box-border min-w-0 overflow-x-auto overflow-y-auto"
        chartPaneStyle={{
          // Subtract --zoom-panel-width (0px when the panel isn't mounted,
          // its current width otherwise) so the chart-content column ends
          // at the ZoomPanel's left edge. Without this the wrapper extended
          // under the fixed ZoomPanel and its vertical scrollbar ended up
          // hidden behind the panel — the user couldn't reach the bottom
          // of long forms (Progress Note ROS rows, Documents lists). Mirrors
          // the equivalent expression on `<CallPage>`'s scroll container.
          height: CHART_CONTENT_HEIGHT_VAR,
          // PD-1164 — the CC panel now reserves via --zoom-panel-width
          // (width-reduction in PatientChartLayout); only the Messages drawer,
          // which doesn't use that var, still pads here.
          paddingRight: isMessagesPanelOpen ? '700px' : undefined,
          transition: 'padding-right 300ms ease-out',
          // When a new encounter is active, the chart pane shares horizontal
          // space with the EncounterWorkspace right pane via flex. In that
          // mode flex owns the sizing — drop the 100vw-derived width that
          // `<PatientChartLayout>` would otherwise apply (it would push the
          // right pane off-screen).
          ...(isNewEncounterActive ? { width: 'auto', flex: 1 } : {}),
        }}
        chartPaneContent={
          // The chart pane ALWAYS renders the navigated chart section. When a
          // new encounter is active the EncounterWorkspace lives as a flex
          // sibling (`rightPane` below), NOT in this pane — so clicking the
          // chart sidebar swaps the chart-section component cleanly without
          // unmounting the workspace (and its live Zoom session).
          currentItem && (
            <currentItem.component
              refetchPatient={refetchPatient}
              patient={patient}
              item={currentItem}
            />
          )
        }
        flexWrapper={
          // Only wrap in a flex container when we actually have a rightPane to
          // place. When no new encounter is active, the layout's default
          // `marginLeft: 280px` on the scroll container handles the offset —
          // wrapping here would double-shift the chart pane by 280+280=560px.
          isNewEncounterActive
            ? (children) => (
                // Mirror CallPage's wrapper: a flex container that starts after
                // the 280px chart sidebar. The chart pane and the workspace
                // pane share this horizontal space as flex siblings.
                <div className="ml-[280px] flex" style={{ width: 'calc(100% - 280px)' }}>
                  {children}
                </div>
              )
            : undefined
        }
        rightPane={
          // Always-mounted while the panel is active: tearing this down between
          // sidebar clicks would (a) unmount `<SidePanelWorkspace>`'s effect
          // that calls `joinSession()` / `leaveSession()`, killing the Zoom
          // session, and (b) discard the in-progress Progress Note form state.
          // The workspace becomes a flex sibling of the chart pane so the
          // doctor can navigate chart sections (Demographics, Labs, etc.) and
          // still see the encounter form on the right.
          //
          // The `<ZoomPanel>` inside is `position: fixed` and anchored to the
          // viewport's right edge; it stays put regardless of this pane's flex
          // sizing. The workspace pane width is a fixed 550px (SIDE_PANEL_WIDTH)
          // so the chart pane keeps a useful working width.
          isNewEncounterActive ? (
            <div
              style={{
                flex: `0 0 ${SIDE_PANEL_WIDTH}px`,
                minWidth: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                height: CHART_CONTENT_HEIGHT_VAR,
                borderLeft: '1px solid #e5e7eb',
              }}
              data-testid="patient-new-encounter-pane"
            >
              <EncounterWorkspace
                parentMode="side-panel"
                patient={patient}
                onFinish={() => {
                  emitPanelEvent('progressNotes:refresh', {
                    kind: 'newestInTab',
                    dataKey: 'progress-notes',
                  })
                  closePanel()
                }}
                onCancel={closePanel}
              />
            </div>
          ) : undefined
        }
        actionBarExtraActions={
          <MessagesActionButton
            isActive={isMessagesPanelOpen}
            onToggle={() => setIsMessagesPanelOpen((open) => !open)}
          />
        }
        extraAfter={
          <MessagesCollapsableContainer
            patient={patient}
            isActive={isMessagesPanelOpen}
            focusThreadId={focusThreadId}
            onClose={() => {
              setIsMessagesPanelOpen(false)
              setFocusThreadId(null)
            }}
          />
        }
      />
      {/* Interrupted-visit recovery card — auto-appears when the provider
        hydrated a recoverable session for THIS patient and no new encounter is
        already active. Resume/Finish hand off to /call (which owns the call +
        finalize machine); Discard runs inline via the provider. */}
      {interruptedSession &&
        interruptedSession.patientId === patient.id &&
        !isNewEncounterActive && (
          <InterruptedResumePanel
            session={interruptedSession}
            busy={resumeBusy}
            callEnded={false}
            onResume={() =>
              navigate(`/call/${interruptedSession.encounterId}?autoJoin=1&intent=resume`)
            }
            onFinish={() =>
              navigate(`/call/${interruptedSession.encounterId}?autoJoin=1&intent=finish`)
            }
            onDiscard={() => {
              setResumeBusy('discard')
              void discardInterrupted().finally(() => setResumeBusy(null))
            }}
          />
        )}
    </>
  )
}
